import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { apiClient, Site } from '../../services/api';

interface DeleteSiteModalProps {
    site: { id: string; name: string; totalDrivers: number } | null;
    onClose: () => void;
    onConfirm: (action: 'unassign' | 'reassign', targetSiteId?: string) => void;
    isDeleting: boolean;
}

const DeleteSiteModal: React.FC<DeleteSiteModalProps> = ({ site, onClose, onConfirm, isDeleting }) => {
    const [availableSites, setAvailableSites] = useState<Site[]>([]);
    const [loadingSites, setLoadingSites] = useState(false);
    const [choice, setChoice] = useState<'reassign' | 'unassign' | null>(null);
    const [targetSiteId, setTargetSiteId] = useState('');

    useEffect(() => {
        if (!site || site.totalDrivers === 0) return;
        setLoadingSites(true);
        apiClient.getSites({ active: true })
            .then(res => setAvailableSites(res.data.filter(s => s.id !== site.id)))
            .catch(() => setAvailableSites([]))
            .finally(() => setLoadingSites(false));
    }, [site]);

    useEffect(() => {
        setChoice(null);
        setTargetSiteId('');
    }, [site?.id]);

    if (!site) return null;

    const hasDrivers = site.totalDrivers > 0;
    const hasOtherSites = availableSites.length > 0;

    const handleConfirm = () => {
        if (!hasDrivers || !hasOtherSites) {
            onConfirm('unassign');
            return;
        }
        if (choice === 'unassign') onConfirm('unassign');
        else if (choice === 'reassign' && targetSiteId) onConfirm('reassign', targetSiteId);
    };

    const isConfirmDisabled =
        isDeleting ||
        loadingSites ||
        (hasDrivers && hasOtherSites && (!choice || (choice === 'reassign' && !targetSiteId)));

    const renderContent = () => {
        if (!hasDrivers) {
            return (
                <>
                    <p className="text-sm text-gray-600 mb-1">
                        Are you sure you want to delete <span className="font-medium text-gray-900">{site.name}</span>?
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        This site has no drivers assigned and can be safely removed.
                    </p>
                </>
            );
        }

        if (loadingSites) {
            return (
                <div className="flex items-center space-x-2 text-gray-500 mb-6">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Checking available sites...</span>
                </div>
            );
        }

        if (!hasOtherSites) {
            return (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        <span className="font-medium text-gray-900">{site.name}</span> has{' '}
                        <span className="font-medium">{site.totalDrivers} driver{site.totalDrivers !== 1 ? 's' : ''}</span> assigned.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                        <p className="text-sm text-amber-800">
                            There are no other active sites to reassign drivers to. All {site.totalDrivers} driver{site.totalDrivers !== 1 ? 's' : ''} will become <strong>unassigned</strong> when this site is deleted.
                        </p>
                    </div>
                </>
            );
        }

        return (
            <>
                <p className="text-sm text-gray-600 mb-4">
                    <span className="font-medium text-gray-900">{site.name}</span> has{' '}
                    <span className="font-medium">{site.totalDrivers} driver{site.totalDrivers !== 1 ? 's' : ''}</span> assigned. Choose what to do with them before deleting:
                </p>

                <div className="space-y-2 mb-6">
                    <label className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${choice === 'reassign' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input
                            type="radio"
                            name="driverAction"
                            value="reassign"
                            checked={choice === 'reassign'}
                            onChange={() => setChoice('reassign')}
                            className="mt-0.5"
                            disabled={isDeleting}
                        />
                        <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">Move all drivers to another site</span>
                            {choice === 'reassign' && (
                                <select
                                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    style={{ '--tw-ring-color': 'rgba(105,190,40,0.5)' } as React.CSSProperties}
                                    value={targetSiteId}
                                    onChange={e => setTargetSiteId(e.target.value)}
                                    disabled={isDeleting}
                                >
                                    <option value="">Select a site...</option>
                                    {availableSites.map(s => (
                                        <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </label>

                    <label className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${choice === 'unassign' ? 'border-gray-400 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input
                            type="radio"
                            name="driverAction"
                            value="unassign"
                            checked={choice === 'unassign'}
                            onChange={() => setChoice('unassign')}
                            className="mt-0.5"
                            disabled={isDeleting}
                        />
                        <div>
                            <span className="text-sm font-medium text-gray-900">Leave all drivers unassigned</span>
                            <p className="text-xs text-gray-500 mt-0.5">Drivers will remain in the system with no site attached.</p>
                        </div>
                    </label>
                </div>
            </>
        );
    };

    const renderConfirmLabel = () => {
        if (isDeleting) return <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>;
        if (!hasDrivers) return 'Delete Site';
        if (!hasOtherSites) return 'Delete & Unassign Drivers';
        if (choice === 'reassign') return 'Delete & Reassign Drivers';
        if (choice === 'unassign') return 'Delete & Unassign Drivers';
        return 'Delete Site';
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete Site</h3>
                </div>

                {renderContent()}

                <div className="flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                    >
                        {renderConfirmLabel()}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteSiteModal;
